"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.handleContextResource = handleContextResource;
const getState_1 = require("../../../app/dist/getState");
const assembleContext_1 = require("../../../app/dist/context/assembleContext");
const serializeBundle_1 = require("../../../app/dist/context/serializeBundle");
const dist_1 = require("../../../fs/dist");
const dist_2 = require("../../../core/dist");
const dist_3 = require("../../../fs/dist");
const fs = __importStar(require("fs-extra"));
const VALID_MODES = ['chat', 'idea', 'design', 'plan', 'implementing', 'refine', 'promote', 'ctx'];
/**
 * Parse the Context Dispatcher ledger from the `loaded` query param.
 * Format: comma-separated `id@version` tokens (e.g. `loaded=loom-ctx@3,de_x@2`).
 * Malformed tokens are dropped — a bad ledger entry must never suppress a doc
 * (failing toward re-injection, never silent under-load). Ids are resolved to
 * canonical form inside assembleContext, so a slug here still matches.
 */
function parseLoadedLedger(param) {
    if (!param)
        return [];
    return param
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .map((token) => {
        const at = token.lastIndexOf('@');
        if (at <= 0)
            return null;
        const id = token.slice(0, at);
        const version = parseInt(token.slice(at + 1), 10);
        if (!id || Number.isNaN(version))
            return null;
        return { id, version };
    })
        .filter((x) => x !== null);
}
/**
 * loom://context/{docUlid}?mode={mode}              (ULID form — strict)
 * loom://context/thread/{weaveSlug}/{threadSlug}?mode={mode}   (slug form — human-pointable)
 *
 * The Unified Context Pipeline delivery point. Builds LoomState (the one impure
 * boundary), runs the pure assembler, and returns the serialised, agent-agnostic
 * markdown bundle. Replaces the legacy loom://thread-context bundling entirely.
 *
 * Two explicit forms, each strict about its own input (naming rule 2 — separate
 * forms, never a dual-accept param):
 *  - ULID form: loom://context/{docUlid} — anchor on a document's canonical id.
 *  - Slug form (human-pointable): loom://context/thread/{weaveSlug}/{threadSlug}
 *    anchors on a thread's primary doc; loom://context/{weaveSlug}/{threadSlug}/{docSlug}
 *    anchors on a named doc within a thread. Slugs resolve → canonical id via the
 *    link index.
 */
function resolveThreadOrThrow(state, weaveSlug, threadSlug) {
    const weave = state.weaves.find(w => w.id === weaveSlug);
    const thread = weave?.threads.find(t => t.id === threadSlug);
    if (!thread)
        throw new Error(`Thread not found: ${weaveSlug}/${threadSlug}`);
    return thread;
}
/**
 * Resolve a document within a thread from a human-pointable doc slug: the canonical
 * singletons by keyword (idea/design/req), else a match on a doc's own id, its `slug`,
 * or its **filename stem** (plan-NNN, chat-NNN, references — the name a human sees on
 * disk and types, e.g. `chat-001.md`). A trailing `.md` is tolerated on every form,
 * and matching is case-insensitive.
 */
function resolveThreadDocBySlug(thread, docSlug) {
    const s = docSlug.toLowerCase().replace(/\.md$/, '');
    if (s === 'idea')
        return thread.idea;
    if (s === 'design')
        return thread.design;
    if (s === 'req')
        return thread.req;
    const stemOf = (d) => d._path ? d._path.split(/[\\/]/).pop().replace(/\.md$/i, '').toLowerCase() : undefined;
    const matches = (d) => d.id.toLowerCase() === s ||
        d.slug?.toLowerCase() === s ||
        stemOf(d) === s;
    // Search every doc collection — allDocs is the union, but plans/chats/refDocs are
    // listed explicitly so resolution never depends on how allDocs was populated.
    const candidates = [
        ...thread.allDocs,
        ...thread.plans,
        ...thread.chats,
        ...thread.refDocs,
    ];
    return candidates.find(matches);
}
async function handleContextResource(root, uri) {
    const url = new URL(uri.replace('loom://', 'loom://host/'));
    // loom://context/...  →  pathname "/context/..."
    const segments = url.pathname.replace(/^\//, '').split('/').map(s => decodeURIComponent(s));
    const modeParam = url.searchParams.get('mode') ?? 'chat';
    const mode = VALID_MODES.includes(modeParam) ? modeParam : 'chat';
    const registry = new dist_3.ConfigRegistry();
    const state = await (0, getState_1.getState)({
        getActiveLoomRoot: dist_1.getActiveLoomRoot,
        loadWeave: dist_1.loadWeave,
        buildLinkIndex: dist_1.buildLinkIndex,
        registry,
        fs,
        workspaceRoot: root,
    });
    let targetId;
    if (segments.length === 4) {
        // Both slug forms carry four path segments — the thread form and the
        // path-qualified doc form — distinguished by the `thread` marker.
        if (segments[1] === 'thread') {
            // Slug form (thread): loom://context/thread/{weaveSlug}/{threadSlug}
            const [, , weaveSlug, threadSlug] = segments;
            const thread = resolveThreadOrThrow(state, weaveSlug, threadSlug);
            const primary = thread.design ??
                thread.idea ??
                thread.plans.find(p => p.status === 'implementing') ??
                thread.plans.find(p => p.status === 'active') ??
                thread.plans[0] ??
                thread.allDocs[0];
            if (!primary)
                throw new Error(`Thread ${weaveSlug}/${threadSlug} has no documents to anchor context`);
            targetId = primary.id;
        }
        else {
            // Slug form (doc): loom://context/{weaveSlug}/{threadSlug}/{docSlug}
            const [, weaveSlug, threadSlug, docSlug] = segments;
            const thread = resolveThreadOrThrow(state, weaveSlug, threadSlug);
            const doc = resolveThreadDocBySlug(thread, docSlug);
            if (!doc)
                throw new Error(`No document "${docSlug}" in thread ${weaveSlug}/${threadSlug}`);
            targetId = doc.id;
        }
    }
    else {
        // ULID form (canonical): loom://context/{docUlid}
        targetId = segments.slice(1).join('/');
        if (!targetId) {
            throw new Error('loom://context requires a target: loom://context/{docUlid}, loom://context/thread/{weaveSlug}/{threadSlug}, or loom://context/{weaveSlug}/{threadSlug}/{docSlug}');
        }
    }
    // Persisted sidebar overrides (Phase 3) — keyed by the canonical target id, the
    // same id the sidebar gets back as bundle.targetId. This is the one impure read
    // (file IO) that feeds the pure assembler; every context consumer (do-step,
    // generate, prompts) routes through this handler and so honours prefs uniformly.
    const canonicalId = (0, dist_2.resolveId)(state.index, targetId) ?? targetId;
    const overrides = await (0, dist_1.readContextPrefsEntry)(root, canonicalId);
    // Context Dispatcher (model C): the caller declares the {id@version} set it
    // already holds via `?loaded=…`; the assembler returns only the delta + a
    // manifest of what it assumed present. Absent param ⇒ empty ledger ⇒ full bundle.
    const alreadyLoaded = parseLoadedLedger(url.searchParams.get('loaded'));
    const bundle = (0, assembleContext_1.assembleContext)(targetId, mode, overrides, state, alreadyLoaded);
    // The sidebar needs the structured bundle (reasons, stale/missing/locked flags,
    // per-doc token estimates) to render its marks; prompt injection needs the
    // agent-agnostic markdown. Same bundle, two encodings — `?format=json` selects
    // the structured form. Default stays markdown so existing callers are unchanged.
    if (url.searchParams.get('format') === 'json') {
        return {
            contents: [{ uri, mimeType: 'application/json', text: JSON.stringify(bundle) }],
        };
    }
    const text = (0, serializeBundle_1.serializeBundle)(bundle);
    return {
        contents: [{ uri, mimeType: 'text/plain', text }],
    };
}
